import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { IconComponent } from '../../../../icon/icon.component';

/**
 * KAN-77 (AC1/AC2): pre-submit question-status grid ("Gatekeeper" review aid).
 *
 * Renders a numbered cell per question (#1..#N) with a clear answered status:
 * green + check for answered, neutral/empty for unanswered. Clicking a cell
 * emits the question index so the parent can scroll the learner to it.
 *
 * The parent computes the `answered` flags via the shared `isQuestionAnswered`
 * predicate, so the grid always agrees with the submit gate.
 */
@Component({
  selector: 'app-question-status-grid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconComponent],
  templateUrl: './question-status-grid.component.html',
  styleUrls: ['./question-status-grid.component.scss'],
})
export class QuestionStatusGridComponent {
  /** Total number of questions to render cells for. */
  @Input() questionCount = 0;
  /** answered[i] === true when question i has a recorded answer. */
  @Input() answered: boolean[] = [];

  /** Emits the 0-based index of the clicked question. */
  @Output() jumpToQuestion = new EventEmitter<number>();

  get cells(): number[] {
    return Array.from({ length: this.questionCount }, (_, i) => i);
  }

  get answeredCount(): number {
    return this.cells.reduce((sum, i) => sum + (this.isAnswered(i) ? 1 : 0), 0);
  }

  get allAnswered(): boolean {
    return this.questionCount > 0 && this.answeredCount === this.questionCount;
  }

  isAnswered(index: number): boolean {
    return this.answered[index] === true;
  }

  onCellClick(index: number): void {
    this.jumpToQuestion.emit(index);
  }

  trackByIndex(index: number): number {
    return index;
  }
}
